import { useRef } from "react";
import html2canvas from "html2canvas";
import Swal from "sweetalert2";
import { trackButtonClick } from "../../../services/googleSheetsService";

/**
 * 配速手環下載/分享功能的 Hook
 * @param {Object} config - 配置選項
 * @param {Function} config.checkInput - 輸入驗證函式
 * @param {string} config.filename - 下載檔名
 * @param {string} config.shareTrackingEvent - 分享追蹤事件名稱
 * @param {string} config.downloadTrackingEvent - 下載追蹤事件名稱
 */
export const usePaceTimerDownload = (config = {}) => {
    const {
        checkInput,
        filename = "配速手環.jpg",
        shareTrackingEvent = "分享配速手環",
        downloadTrackingEvent = "下載配速手環",
    } = config;

    const printableRef = useRef();

    // * 儲存圖片函式（支援 Share API 和傳統下載）
    const saveImage = async (imageDataUrl, filenameToUse) => {
        const isMobileOrTablet = window.innerWidth <= 1024;

        // 手機/平板且支援 Share API
        if (isMobileOrTablet && navigator.share && navigator.canShare) {
            try {
                const blob = await (await fetch(imageDataUrl)).blob();
                const file = new File([blob], filenameToUse, { type: "image/jpeg" });

                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        files: [file],
                        title: "配速手環",
                        text: "我的馬拉松配速手環",
                    });
                    if (shareTrackingEvent) {
                        trackButtonClick(shareTrackingEvent, "Share");
                    }
                    return true;
                }
            } catch (err) {
                if (err.name === "AbortError") {
                    // 使用者取消分享，不顯示錯誤
                    return false;
                }
                console.log("Share API failed, fallback to download", err);
            }
        }

        // 下載圖片
        if (downloadTrackingEvent) {
            trackButtonClick(downloadTrackingEvent, "Download");
        }
        const link = document.createElement("a");
        link.href = imageDataUrl;
        link.download = filenameToUse;
        link.click();
        return true;
    };

    // * 下載圖片設定
    const handleDownloadImage = async () => {
        if (checkInput && checkInput()) {
            return;
        }

        // 顯示載入中訊息
        Swal.fire({
            title: "準備下載圖片中...",
            html: "請稍候",
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
        });

        const element = printableRef.current;

        // 暫時顯示元素並加上用於下載的 class（不會顯示在畫面上）
        const originalDisplay = element.style.display;
        const originalPosition = element.style.position;
        const originalLeft = element.style.left;
        const originalTop = element.style.top;

        // 強制設定固定尺寸和位置，確保所有設備渲染一致
        element.style.display = "block";
        element.style.position = "fixed";
        element.style.left = "-9999px";
        element.style.top = "0";
        element.style.width = "1200px";
        element.style.height = "1800px";
        element.classList.add("download-46-format");

        // 等待瀏覽器完成樣式計算和渲染
        await new Promise((resolve) => setTimeout(resolve, 100));

        const canvas = await html2canvas(element, {
            scale: 3, // 固定 scale = 3，確保所有設備輸出一致 (3600 x 5400 像素)
            width: 1200,
            height: 1800,
            windowWidth: 1200,
            windowHeight: 1800,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            onclone: (clonedDoc) => {
                // 在複製的文件中強制設定固定樣式
                const clonedElement = clonedDoc.querySelector(".download-46-format");
                if (clonedElement) {
                    clonedElement.style.width = "1200px";
                    clonedElement.style.height = "1800px";
                    clonedElement.style.fontSize = "16px";
                }
            },
        });

        // 恢復原始狀態
        element.classList.remove("download-46-format");
        element.style.display = originalDisplay;
        element.style.position = originalPosition;
        element.style.left = originalLeft;
        element.style.top = originalTop;
        element.style.width = "";
        element.style.height = "";

        // 使用 JPG 格式減少檔案大小
        const imageDataUrl = canvas.toDataURL("image/jpeg", 0.95);

        // 判斷是否為手機或平板
        const isMobileOrTablet = window.innerWidth <= 1024;

        // 檢查是否支援 Share API
        const supportsShare = isMobileOrTablet && navigator.share && navigator.canShare;

        // 根據裝置類型調整提示訊息
        let hintMessage = "";
        if (isMobileOrTablet) {
            hintMessage = supportsShare
                ? '<p style="margin-top: 10px; color: #831f1fff; margin-bottom: 0px;">點選「儲存/分享」按鈕或長按圖片儲存！</p>'
                : '<p style="margin-top: 10px; color: #831f1fff; margin-bottom: 0px;">請長按圖片儲存到相簿！</p>';
        }

        // 顯示預覽彈窗
        const result = await Swal.fire({
            title: "配速手環預覽",
            html: `
            ${hintMessage}
            </br>
            <img src="${imageDataUrl}" style="max-width: 100%; height: auto; border: 1px solid #ddd; border-radius: 8px;" />
            `,
            width: isMobileOrTablet ? "95%" : "500px",
            padding: "0.7rem",
            showCancelButton: true,
            confirmButtonText: isMobileOrTablet
                ? supportsShare
                    ? "儲存/分享"
                    : "下載"
                : "確認下載",
            cancelButtonText: "取消",
            showConfirmButton: true, // 所有裝置都顯示按鈕
            customClass: {
                confirmButton: "custom-confirm-button",
            },
        });

        // 如果使用者點擊確認，才下載/分享圖片
        if (result.isConfirmed) {
            const success = await saveImage(imageDataUrl, filename);

            if (success) {
                Swal.fire({
                    title: supportsShare ? "完成！" : "下載成功！",
                    text: supportsShare ? "已開啟分享選單" : "圖片已開始下載",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            }
        }
    };

    return {
        printableRef,
        handleDownloadImage,
    };
};
